import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { CompanyCard } from "../components/CompanyCard";
import { DEPARTMENT_NAMES } from "../lib/regions";

export default function DepartmentDirectory() {
  const { code } = useParams();
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const deptName = DEPARTMENT_NAMES[code] || `Département ${code}`;

  useEffect(() => {
    setLoading(true);
    api.get("/companies", { params: { department: code, limit: 200 } }).then((r) => {
      setCompanies(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setLoading(false);
    });
    document.title = `Déménageurs ${code} ${deptName} — Déménageur Pro`;
  }, [code, deptName]);

  return (
    <div className="animate-fade-up">
      <section className="bg-gradient-to-b from-[#F7F9FB] to-white py-16 md:py-20">
        <div className="container mx-auto px-6 lg:px-10">
          <div className="text-xs text-[#6B7280] mb-5 flex items-center gap-2">
            <Link to="/" className="hover:text-[#2BD391]">Accueil</Link>
            <span>/</span>
            <Link to="/villes" className="hover:text-[#2BD391]">Régions</Link>
            <span>/</span>
            <span className="text-[#2C3E50] font-semibold">{code} — {deptName}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E8FBF3] text-[#1AA66C] text-xs font-semibold mb-4">
                <ShieldCheck className="w-3.5 h-3.5" /> Département {code}
              </div>
              <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight text-[#2C3E50] mb-4 leading-tight">
                Déménageurs<br/><span className="text-[#2BD391]">{deptName}</span> ({code})
              </h1>
              <p className="text-[#6B7280] max-w-2xl text-lg">
                <b className="text-[#2C3E50]">{total}</b> entreprises de déménagement référencées dans le département {code} ({deptName}).
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/devis" className="btn-primary btn-large">
                Recevoir des devis <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="text-center text-sm text-[#6B7280] py-20">Chargement…</div>
          ) : companies.length === 0 ? (
            <div className="card-clean text-center max-w-xl mx-auto">
              <div className="w-16 h-16 bg-[#E8FBF3] rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-[#2BD391]" />
              </div>
              <h2 className="font-heading font-extrabold text-2xl mb-2 text-[#2C3E50]">Aucune entreprise dans le {code}</h2>
              <p className="text-[#6B7280] mb-6">Faites votre demande, nous trouverons les meilleurs déménageurs.</p>
              <Link to="/devis" className="btn-primary">Demander un devis</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {companies.map((c) => <CompanyCard key={c.id} company={c} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
